import { Input, Select, Label, HelperText } from './../ui';
import { Lock } from 'lucide-react';
import { TagPicker } from '../TagPicker';
import type { Profile } from '@scholarship-app/types';

interface Props {
  draft: Profile;
  editing: boolean;
  isApplicantRole: boolean;
  onChange: (next: Profile) => void;
}

const ETHNICITY_SUGGESTIONS = [
  'hispanic', 'white', 'black', 'asian', 'native_american',
  'pacific_islander', 'middle_eastern', 'two_or_more_races',
];

const LANGUAGE_SUGGESTIONS = [
  'english', 'spanish_conversational', 'spanish_fluent', 'french', 'mandarin',
  'german', 'italian', 'portuguese', 'japanese', 'korean', 'arabic',
];

export function ProfileDemographicsEditor({ draft, editing, isApplicantRole, onChange }: Props) {
  const parentOnlyDisabled = isApplicantRole;
  return (
    <div className="space-y-4">
      {/* Religion */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Religious affiliation</Label>
          <Select
            value={draft.religious_affiliation ?? ''}
            onChange={(e) => onChange({ ...draft, religious_affiliation: e.target.value || null })}
            disabled={!editing}
          >
            <option value="">— None / not specified —</option>
            <option value="lutheran">Lutheran</option>
            <option value="catholic">Catholic</option>
            <option value="protestant">Protestant</option>
            <option value="evangelical">Evangelical</option>
            <option value="orthodox">Orthodox</option>
            <option value="jewish">Jewish</option>
            <option value="muslim">Muslim</option>
            <option value="hindu">Hindu</option>
            <option value="buddhist">Buddhist</option>
            <option value="other">Other</option>
          </Select>
        </div>
        <div>
          <Label>
            Lutheran synod (if applicable) {parentOnlyDisabled && <Lock size={12} className="inline ml-1 text-amber-600" />}
          </Label>
          <Input
            value={draft.lutheran_synod ?? ''}
            onChange={(e) => onChange({ ...draft, lutheran_synod: e.target.value || null })}
            disabled={!editing || parentOnlyDisabled}
            placeholder="e.g. LCMS, ELCA"
          />
        </div>
        <div>
          <Label>Church name</Label>
          <Input
            value={draft.church_name ?? ''}
            onChange={(e) => onChange({ ...draft, church_name: e.target.value || null })}
            disabled={!editing}
          />
        </div>
        <div>
          <Label>Church city</Label>
          <Input
            value={draft.church_city ?? ''}
            onChange={(e) => onChange({ ...draft, church_city: e.target.value || null })}
            disabled={!editing}
          />
        </div>
      </div>

      {/* Ethnicity */}
      <div>
        <Label>Ethnicity</Label>
        <TagPicker
          values={draft.ethnicity}
          onChange={(values) => onChange({ ...draft, ethnicity: values })}
          suggestions={ETHNICITY_SUGGESTIONS}
          disabled={!editing}
          placeholder="Add ethnicity"
        />
      </div>

      {/* Languages */}
      <div>
        <Label>Languages spoken</Label>
        <TagPicker
          values={draft.languages_spoken}
          onChange={(values) => onChange({ ...draft, languages_spoken: values })}
          suggestions={LANGUAGE_SUGGESTIONS}
          disabled={!editing}
          placeholder="Add a language"
        />
      </div>

      {/* First-gen / parent education / income */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>First-generation college student?</Label>
          <Select
            value={draft.first_generation_college_student ? 'yes' : 'no'}
            onChange={(e) => onChange({ ...draft, first_generation_college_student: e.target.value === 'yes' })}
            disabled={!editing}
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </Select>
        </div>
        <div>
          <Label>
            Household income bracket {parentOnlyDisabled && <Lock size={12} className="inline ml-1 text-amber-600" />}
          </Label>
          <Select
            value={draft.household_income_bracket ?? ''}
            onChange={(e) => onChange({ ...draft, household_income_bracket: (e.target.value || null) as Profile['household_income_bracket'] })}
            disabled={!editing || parentOnlyDisabled}
          >
            <option value="">— Not specified —</option>
            <option value="under_30k">Under $30k</option>
            <option value="30k_to_60k">$30k–$60k</option>
            <option value="60k_to_100k">$60k–$100k</option>
            <option value="100k_to_150k">$100k–$150k</option>
            <option value="150k_to_200k">$150k–$200k</option>
            <option value="over_200k">Over $200k</option>
          </Select>
        </div>
      </div>

      {/* Parent education */}
      <div>
        <Label>
          Parent education levels {parentOnlyDisabled && <Lock size={12} className="inline ml-1 text-amber-600" />}
        </Label>
        <div className="space-y-2">
          {(draft.parent_education_levels ?? []).map((pe, i) => (
            <div key={i} className="flex gap-2 items-center">
              <Input
                value={pe.parent_label}
                onChange={(e) => {
                  const next = [...(draft.parent_education_levels ?? [])];
                  next[i] = { ...next[i], parent_label: e.target.value };
                  onChange({ ...draft, parent_education_levels: next });
                }}
                disabled={!editing || parentOnlyDisabled}
                placeholder="Parent name"
              />
              <Select
                value={pe.highest_degree}
                onChange={(e) => {
                  const next = [...(draft.parent_education_levels ?? [])];
                  next[i] = { ...next[i], highest_degree: e.target.value as typeof pe.highest_degree };
                  onChange({ ...draft, parent_education_levels: next });
                }}
                disabled={!editing || parentOnlyDisabled}
              >
                <option value="high_school">High School</option>
                <option value="some_college">Some College</option>
                <option value="associates">Associate&apos;s</option>
                <option value="bachelors">Bachelor&apos;s</option>
                <option value="masters">Master&apos;s</option>
                <option value="doctorate">Doctorate</option>
              </Select>
              {editing && !parentOnlyDisabled && (
                <button
                  type="button"
                  className="text-rose-600 text-xs px-2 hover:underline"
                  onClick={() => {
                    const next = (draft.parent_education_levels ?? []).filter((_, idx) => idx !== i);
                    onChange({ ...draft, parent_education_levels: next });
                  }}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          {editing && !parentOnlyDisabled && (
            <button
              type="button"
              className="text-forest-700 text-sm hover:underline"
              onClick={() => {
                const next = [...(draft.parent_education_levels ?? []), { parent_label: '', highest_degree: 'bachelors' as const }];
                onChange({ ...draft, parent_education_levels: next });
              }}
            >
              + Add parent
            </button>
          )}
        </div>
      </div>

      {parentOnlyDisabled && (
        <HelperText>Fields marked with a lock icon are parent-only and can&apos;t be edited from this account.</HelperText>
      )}
    </div>
  );
}
